import { clamp } from '@suntex.core/math';
import { Common_VifModule } from '@suntex.fim/api-client';
import { VifModuleInfo } from '@suntex.fim/enums';

import type { BlockFlag } from './dom/core/blockConfig';
import {
  type RulesAddSubFlag,
  isRulesAddSubFlag,
} from './dom/core/rulesAddSubProblems';
import {
  type RulesMulDivFlag,
  isRulesMulDivFlag,
} from './dom/core/rulesMulDivProblems';
import { lookupBlockGameFlag } from './games/engine/blockFlag';
import { lookupRulesAddSubGameFlag } from './games/engine/rulesAddSubFlag';
import { lookupRulesMulDivGameFlag } from './games/engine/rulesMulDivFlag';

export interface VifGameModule extends Common_VifModule {
  moduleId: VifModuleInfo;
  moduleVariant: number;
  slug: string;
}

export interface VifGearsModule extends Partial<Common_VifModule> {
  filename: 'VIFGears';
  moduleId: VifModuleInfo.none;
  moduleVariant: number;
  slug?: undefined;
}

/** A canvas game module is any VIF module with a (non-zero) moduleId. */
export function isVifGameModule(
  vifModule: Common_VifModule,
): vifModule is VifGameModule {
  return !!vifModule.moduleId;
}

/** The Gears module is identified by its filename and has no moduleId. */
export function isVifGearsModule(
  vifModule: Common_VifModule,
): vifModule is VifGearsModule & Common_VifModule {
  return vifModule.filename === 'VIFGears';
}

/**
 * Whether a module should render on the DOM rules board instead of the
 * CreateJS canvas.
 *
 * Gated on the `VIFRulesAddSub` or `VIFRulesMulDiv` composition plus a known
 * `gameFlag` for that composition (0-12 for add/sub, 0-8/10-16 for mul/div),
 * so every other VIF module (the 4x4 block games, `VIFRedoAddSub`,
 * `VIFGears`) keeps the canvas path and cannot regress. Widening this gate is
 * how the DOM board rolls out.
 */
export function isVifRulesDomModule(
  vifModule: Common_VifModule,
): vifModule is VifGameModule {
  if (!isVifGameModule(vifModule)) {
    return false;
  }

  if (vifModule.filename === 'VIFRulesAddSub') {
    const flag = lookupRulesAddSubGameFlag(
      vifModule.moduleId,
      vifModule.moduleVariant,
    );
    return flag !== null && isRulesAddSubFlag(flag);
  }

  if (vifModule.filename === 'VIFRulesMulDiv') {
    const flag = lookupRulesMulDivGameFlag(
      vifModule.moduleId,
      vifModule.moduleVariant,
    );
    return flag !== null && isRulesMulDivFlag(flag);
  }

  return false;
}

/** The DOM board's `gameFlag`. Only valid for `isVifRulesDomModule` modules. */
export function vifRulesAddSubFlag(vifModule: VifGameModule): RulesAddSubFlag {
  const flag = lookupRulesAddSubGameFlag(
    vifModule.moduleId,
    vifModule.moduleVariant,
  );

  if (flag === null || !isRulesAddSubFlag(flag)) {
    throw new Error(`Not a DOM-rendered VIF rules variant: gameFlag ${flag}`);
  }

  return flag;
}

/** The DOM board's `gameFlag`. Only valid for `isVifRulesDomModule` modules. */
export function vifRulesMulDivFlag(vifModule: VifGameModule): RulesMulDivFlag {
  const flag = lookupRulesMulDivGameFlag(
    vifModule.moduleId,
    vifModule.moduleVariant,
  );

  if (flag === null || !isRulesMulDivFlag(flag)) {
    throw new Error(`Not a DOM-rendered VIF rules variant: gameFlag ${flag}`);
  }

  return flag;
}

/**
 * Whether a module should render on the DOM block board instead of the
 * CreateJS canvas.
 *
 * Gated on the `VIFAddSub` or `VIFMulDiv` composition plus moduleId 166-169
 * variant 0 (see `lookupBlockGameFlag`) - the same moduleIds at other
 * variants are the unrelated 3x3 "rules" stages `isVifRulesDomModule` already
 * covers.
 */
export function isVifBlockDomModule(
  vifModule: Common_VifModule,
): vifModule is VifGameModule {
  if (!isVifGameModule(vifModule)) {
    return false;
  }

  if (
    vifModule.filename !== 'VIFAddSub' &&
    vifModule.filename !== 'VIFMulDiv'
  ) {
    return false;
  }

  return (
    lookupBlockGameFlag(vifModule.moduleId, vifModule.moduleVariant) !== null
  );
}

/** The DOM board's `gameFlag`. Only valid for `isVifBlockDomModule` modules. */
export function vifBlockFlag(vifModule: VifGameModule): BlockFlag {
  const flag = lookupBlockGameFlag(vifModule.moduleId, vifModule.moduleVariant);

  if (flag === null) {
    throw new Error(`Not a DOM-rendered VIF block variant: gameFlag ${flag}`);
  }

  return flag;
}

/**
 * Resolves which VIF stage to display and how far the sequence line should fill.
 *
 * Replaces the inline logic in Suntex-Frontend's `Vif.tsx`, which had a bug: it
 * computed `Math.max(actualStage, 1)` and then immediately discarded it by
 * overwriting `stage` with `Math.min(actualStage, max)`. For non-player logins
 * (where `actualStage` was -1) that produced a negative index and crashed the
 * page. Here the value is clamped to `[1, moduleCount]` once, and non-players
 * navigate by the URL-provided stage (defaulting to the first stage) instead of
 * crashing.
 *
 * @param passedStage  Stage from the URL (0 if none provided).
 * @param savedStage   The player's saved stage from the API.
 * @param preview      If previewing, vs playing
 * @param moduleCount  Number of stages available for the operation.
 */
export function resolveStage({
  passedStage,
  savedStage,
  preview,
  moduleCount,
}: {
  passedStage: number;
  savedStage: number;
  preview: boolean;
  moduleCount: number;
}): { stage: number; maxAllowedStage: number } {
  let actualStage: number;

  if (!preview) {
    actualStage = savedStage;
    // A legal lower stage may be passed (e.g. for practice).
    if (0 < passedStage && passedStage < savedStage) {
      actualStage = passedStage;
    }
  } else {
    // Admin / preview logins navigate directly to a stage via the URL.
    actualStage = passedStage;
  }

  const stage = clamp(actualStage, [1, Math.max(moduleCount, 1)]);
  const maxAllowedStage = preview
    ? moduleCount
    : clamp(savedStage, [1, Math.max(moduleCount, 1)]);

  return { stage, maxAllowedStage };
}
